import { useState, useMemo, useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Sparkles, Wallet, Briefcase, Landmark, Bot, Layers, Brain, LineChart,
  Bell, Gift, Settings as SettingsIcon, LifeBuoy, Search, Download, Printer,
  ChevronRight,
} from "lucide-react";
import { MANUAL_SECTIONS, buildFullManualMarkdown } from "../lib/manual";

const ICONS = {
  sparkles: Sparkles, wallet: Wallet, briefcase: Briefcase, landmark: Landmark,
  bot: Bot, layers: Layers, brain: Brain, "line-chart": LineChart, bell: Bell,
  gift: Gift, settings: SettingsIcon, "life-buoy": LifeBuoy,
};

export default function Help() {
  const [active, setActive] = useState(MANUAL_SECTIONS[0].id);
  const [query, setQuery] = useState("");

  // Sync from ?section=xyz URL param so /help?section=bots works from anywhere
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("section");
    if (p && MANUAL_SECTIONS.some((s) => s.id === p)) setActive(p);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return MANUAL_SECTIONS;
    const q = query.toLowerCase();
    return MANUAL_SECTIONS.filter(
      (s) => s.title.toLowerCase().includes(q) || s.body.toLowerCase().includes(q)
    );
  }, [query]);

  const activeSection = MANUAL_SECTIONS.find((s) => s.id === active) || MANUAL_SECTIONS[0];

  const downloadMarkdown = () => {
    const blob = new Blob([buildFullManualMarkdown()], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "aanar-manual.md";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const openPrintable = () => {
    window.open("/manual", "_blank");
  };

  return (
    <div className="space-y-4" data-testid="help-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Instruction manual</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">How to use Aanar</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every screen explained. Skim it, search it, or print the whole thing.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={downloadMarkdown} data-testid="help-download-md"
                  className="rounded-full text-xs">
            <Download className="w-3.5 h-3.5 mr-1.5" /> Markdown
          </Button>
          <Button variant="outline" onClick={openPrintable} data-testid="help-print"
                  className="rounded-full text-xs">
            <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-4">
        {/* Sidebar */}
        <Card className="p-3 rounded-2xl border-border h-fit lg:sticky lg:top-4" data-testid="help-sidebar">
          <div className="relative mb-2">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the manual…"
              data-testid="help-search"
              className="pl-8 h-8 text-xs bg-secondary/30 border-border/60"
            />
          </div>
          <nav className="space-y-0.5">
            {filtered.length === 0 && (
              <div className="text-[11px] text-muted-foreground italic p-2">No matches — try a different keyword</div>
            )}
            {filtered.map((s) => {
              const Icon = ICONS[s.icon] || Sparkles;
              const isActive = s.id === active;
              return (
                <button
                  key={s.id}
                  data-testid={`help-nav-${s.id}`}
                  onClick={() => {
                    setActive(s.id);
                    window.history.replaceState(null, "", `?section=${s.id}`);
                    const el = document.querySelector('[data-testid="help-content"]');
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-lg text-left text-xs transition-colors ${
                    isActive
                      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/25"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary/50 border border-transparent"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  <span className="flex-1 truncate">{s.title}</span>
                  {isActive && <ChevronRight className="w-3 h-3" />}
                </button>
              );
            })}
          </nav>
        </Card>

        {/* Content */}
        <Card className="p-6 md:p-8 rounded-2xl border-border" data-testid="help-content">
          <article className="prose prose-invert max-w-none prose-headings:font-display prose-h1:text-3xl prose-h1:mb-6 prose-h2:text-lg prose-h2:mt-6 prose-h2:mb-2 prose-p:text-sm prose-p:leading-relaxed prose-li:text-sm prose-li:leading-relaxed prose-strong:text-foreground prose-code:text-emerald-300 prose-code:bg-emerald-500/10 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{activeSection.body}</ReactMarkdown>
          </article>
        </Card>
      </div>
    </div>
  );
}
